import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import _ from 'lodash'
import {
  Box,
  VStack,
  FormControl as ChakraFormControl,
  FormErrorMessage as ChakraFormErrorMessage,
} from '@chakra-ui/react'
import type { OnChangeValue } from 'react-select'

import { IKReactSelectCreatable } from '../../../../ui-kit'
import type { InputFormProps as Props } from '../../../interfaces'
import type { CreatedByOption } from '../../../interfaces/MediaLibrary'
import {
  getFieldsMapping,
  validateFilter,
} from '../../../../utils/mediaLibrary'
import OperatorMenu from '../OperatorMenu'
import { useActiveUsers } from '../../../hooks/useActiveUsers'
import { UserContext } from '../../../providers/UserProvider'

interface UserOption {
  value: string
  label: string
}

const CreatedByInputForm = (props: Props): React.ReactElement => {
  const {
    searchQueryJson,
    validationObject,
    groupId,
    filterId,
    initialFocusRef,
    setSearchQueryJson,
  } = props

  const { users: activeUsers } = useActiveUsers()
  const { user } = useContext(UserContext)

  const API_USER_SENTINEL = '__API_USER__'

  const userOptions = useMemo<UserOption[]>(
    () => {
      const mapped = activeUsers.map(u => ({
        value: u.userEmail,
        label: u.userEmail === user?.userEmail
          ? `Created by me`
          : `${u.userName} (${u.userEmail})`,
      }))
      mapped.sort((a, b) => {
        if (a.value === user?.userEmail) return -1
        if (b.value === user?.userEmail) return 1
        return 0
      })
      return [
        ...mapped,
        { value: API_USER_SENTINEL, label: 'API user' },
      ]
    },
    [activeUsers, user?.userEmail],
  )

  const resolveLabel = useCallback(
    (email: string): string => {
      const match = userOptions.find(opt => opt.value === email)
      return match ? match.label : email
    },
    [userOptions],
  )

  const [dirty, setDirty] = useState<boolean>(false)

  const expression = useMemo(
    () => searchQueryJson.filterGroups[groupId]?.expressions[filterId],
    [searchQueryJson, groupId, filterId],
  )

  const error: string | null = useMemo(() => {
    if (dirty) {
      const validation = validateFilter({
        expression,
        customMetadataField: undefined,
      })
      return validation.error
    }
    return validationObject.errors[groupId][filterId]
  }, [validationObject, groupId, filterId, dirty, expression])

  const handleOperatorChange = useCallback(
    (newOperator: string) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      const currentExpr = newSearchQueryJson.filterGroups[groupId].expressions[filterId]
      const oldOperator = currentExpr.operator
      const oldValue = currentExpr.value

      currentExpr.operator = newOperator

      const isSingleSelect = (op: string) => ['IS', 'IS_NOT'].includes(op)
      const isMultiSelect = (op: string) => ['IS_ONE_OF', 'IS_NONE_OF'].includes(op)

      if (isSingleSelect(oldOperator) && isSingleSelect(newOperator)) {
        // Same cardinality (single → single): preserve value
      } else if (isMultiSelect(oldOperator) && isMultiSelect(newOperator)) {
        // Same cardinality (multi → multi): preserve value
      } else if (isSingleSelect(oldOperator) && isMultiSelect(newOperator)) {
        // single → multi: wrap current value in array if it has a value
        const singleVal = oldValue as CreatedByOption
        currentExpr.value = singleVal?.value ? [singleVal] : []
      } else if (isMultiSelect(oldOperator) && isSingleSelect(newOperator)) {
        // multi → single: take first element if available
        const multiVal = oldValue as CreatedByOption[]
        currentExpr.value = Array.isArray(multiVal) && multiVal.length > 0
          ? multiVal[0]
          : { value: '', label: '' }
      } else {
        currentExpr.value = getFieldsMapping({ fieldName: expression.fieldName }).getInitialValue(newOperator)
      }

      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId, expression.fieldName],
  )

  const handleValueChange = useCallback(
    (newValue: CreatedByOption | CreatedByOption[]) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        newValue
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  useEffect(() => {
    initialFocusRef.current?.focus()
  }, [initialFocusRef, expression.operator])

  return (
    <ChakraFormControl width="full" isInvalid={error !== null && dirty}>
      <VStack alignItems="start" width="full" spacing="0.5em">
        <VStack alignItems="stretch" width="full" spacing="0.5em">
          <OperatorMenu
            width="full"
            expression={expression}
            handleOperatorChange={handleOperatorChange}
          />

          {['IS', 'IS_NOT'].includes(expression.operator) && (
            <Box width="full">
              <IKReactSelectCreatable
                ref={initialFocusRef}
                variant="compact"
                placeholder="Select user or type email"
                isClearable
                options={userOptions}
                value={
                  expression.value
                    ? typeof expression.value === 'string'
                      ? { value: expression.value, label: resolveLabel(expression.value) }
                      : (expression.value as CreatedByOption).value
                        ? {
                            value: (expression.value as CreatedByOption).value,
                            label: resolveLabel((expression.value as CreatedByOption).value),
                          }
                        : null
                    : null
                }
                onChange={(
                  selected: OnChangeValue<UserOption, false>,
                ) => {
                  handleValueChange(
                    selected
                      ? { value: selected.value, label: selected.label }
                      : { value: '', label: '' },
                  )
                  setDirty(true)
                }}
                formatCreateLabel={(inputValue: string) =>
                  `Search by email: ${inputValue}`
                }
              />
            </Box>
          )}

          {['IS_ONE_OF', 'IS_NONE_OF'].includes(expression.operator) &&
            Array.isArray(expression.value) && (
              <Box width="full">
                <IKReactSelectCreatable
                  ref={initialFocusRef}
                  variant="compact"
                  placeholder="Select users or type emails"
                  isMulti
                  options={userOptions}
                  value={
                    (expression.value as (CreatedByOption | string)[]).map(item => {
                      const email = typeof item === 'string' ? item : item.value
                      return { value: email, label: resolveLabel(email) }
                    }) ?? []
                  }
                  onChange={(
                    arr: OnChangeValue<UserOption, true>,
                  ) => {
                    const newVal = arr.map(opt => ({
                      value: opt.value,
                      label: opt.label,
                    }))
                    handleValueChange(newVal)
                    setDirty(true)
                  }}
                  formatCreateLabel={(inputValue: string) =>
                    `Search by email: ${inputValue}`
                  }
                />
              </Box>
            )}
        </VStack>

        <ChakraFormErrorMessage maxWidth="full">{error}</ChakraFormErrorMessage>
      </VStack>
    </ChakraFormControl>
  )
}

export default CreatedByInputForm
